<script setup>
import { ref } from 'vue'
import { useAuthApi } from '~/composables/api/useAuthApi.js'
import { useAuthStore } from '~/stores/auth.js'

const props = defineProps({
  token: { type: String, default: '' },
  redirect: { type: String, default: '/dashboard' },
  onSubmit: Function,
  onCancel: Function,
})

const authApi = useAuthApi()
const auth = useAuthStore()
const { t } = useI18n()

const password = ref('')
const passwordRepeated = ref('')
const loading = ref(false)
const error = ref('')
const success = ref(false)

const close = () => props.onCancel?.()

const onConfirm = async () => {
  if (loading.value) return
  if (!password.value || !passwordRepeated.value) {
    error.value = t('auth.passwordReset.errors.missing_fields')
    return
  }
  if (password.value !== passwordRepeated.value) {
    error.value = t('auth.passwordReset.errors.mismatch')
    return
  }
  if (password.value.length < 6) {
    error.value = t('auth.passwordReset.errors.weak_password')
    return
  }
  loading.value = true
  error.value = ''
  try {
    const payload = await authApi.confirmPasswordReset(props.token, password.value)
    await auth.login(payload)
    success.value = true
  } catch (e) {
    const code = e?.message
    const map = {
      invalid_token:    'invalid_token',
      token_expired:    'token_expired',
      weak_password:    'weak_password',
      validation_error: 'missing_fields',
    }
    error.value = t(`auth.passwordReset.errors.${map[code] || 'generic'}`)
  } finally {
    loading.value = false
  }
}

const onSuccessClose = async () => {
  await props.onSubmit?.()
  await navigateTo(props.redirect)
}
</script>

<template>
  <div class="modal-overlay" @click.self="close">
    <div class="modal-box">
      <template v-if="!token">
        <h3 class="font-black text-lg text-ink mb-1.5">{{ $t('auth.passwordReset.errors.invalid_token') }}</h3>
        <p class="text-sm text-muted mb-5 leading-relaxed">{{ $t('auth.passwordReset.errors.no_token_desc') }}</p>
        <button type="button" class="w-full p-3.5 rounded-xl bg-grad-brand text-on-brand font-extrabold text-sm cursor-pointer shadow-[0_4px_16px_rgb(var(--c-brand)/.3)]" @click="close">{{ $t('auth.passwordReset.backHome') }}</button>
      </template>

      <template v-else-if="success">
        <div class="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center mb-4 mx-auto">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#10b981" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
        </div>
        <h3 class="font-black text-lg text-ink mb-1.5 text-center">{{ $t('auth.passwordReset.success.title') }}</h3>
        <p class="text-sm text-muted mb-5 leading-relaxed text-center">{{ $t('auth.passwordReset.success.desc') }}</p>
        <button type="button" class="w-full p-3.5 rounded-xl bg-grad-brand text-on-brand font-extrabold text-sm cursor-pointer shadow-[0_4px_16px_rgb(var(--c-brand)/.3)]" @click="onSuccessClose">{{ $t('auth.passwordReset.success.cta') }}</button>
      </template>

      <template v-else>
        <h3 class="font-black text-lg text-ink mb-1.5">{{ $t('auth.passwordReset.title') }}</h3>
        <p class="text-sm text-muted mb-5 leading-relaxed">{{ $t('auth.passwordReset.desc') }}</p>
        <form @submit.prevent="onConfirm">
          <input v-model="password" class="w-full px-4 py-3 border-[1.5px] border-line rounded-xl text-sm outline-none transition-colors duration-150 mb-2.5 focus:border-brand" type="password" :placeholder="$t('auth.passwordReset.password')" autocomplete="new-password" autofocus />
          <input v-model="passwordRepeated" class="w-full px-4 py-3 border-[1.5px] border-line rounded-xl text-sm outline-none transition-colors duration-150 mb-2.5 focus:border-brand" type="password" :placeholder="$t('auth.passwordReset.passwordRepeated')" autocomplete="new-password" />
          <p v-if="error" class="text-xs text-red-500 mb-2.5">{{ error }}</p>
          <button class="w-full p-3.5 rounded-xl bg-grad-brand text-on-brand font-extrabold text-sm cursor-pointer shadow-[0_4px_16px_rgb(var(--c-brand)/.3)] disabled:opacity-50 disabled:cursor-not-allowed" :disabled="loading" type="submit">{{ loading ? '…' : `${$t('auth.passwordReset.submit')} →` }}</button>
        </form>
      </template>
    </div>
  </div>
</template>
